import React, { useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../../shared/ui/kit/utils';
import { MESSAGE_KIND_CONFIG } from '../constants';
import { formatMessageTime, formatMessageTimeAgo } from '../utils';
import type { Message } from '../types';

interface MessageCardProps {
    message: Message;
    isSelected?: boolean;
    onClick?: () => void;
    onMarkAsRead?: () => void;
    index: number;
}

const iconComponents: Record<string, React.ReactNode> = {
    'alert-circle': (
        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
    ),
    'alert-triangle': (
        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
        </svg>
    ),
    'info': (
        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
    ),
    'check-circle': (
        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
    ),
    'check-circle-2': (
        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9 12l2 2 4-4"/>
        </svg>
    ),
};

export const MessageCard: React.FC<MessageCardProps> = ({
    message,
    isSelected,
    onClick,
    onMarkAsRead,
    index,
}) => {
    const config = MESSAGE_KIND_CONFIG[message.kind];
    const isUnread = message.status === 'unread';

    const formattedTime = useMemo(() => formatMessageTime(message.timestamp), [message.timestamp]);
    const timeAgo = useMemo(() => formatMessageTimeAgo(message.timestamp), [message.timestamp]);

    const handleClick = useCallback(() => {
        if (isUnread && onMarkAsRead) {
            onMarkAsRead();
        }
        onClick?.();
    }, [isUnread, onClick, onMarkAsRead]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, x: -20, scale: 0.95 }}
            transition={{ 
                duration: 0.3, 
                delay: index * 0.03,
                type: 'spring',
                stiffness: 100,
                damping: 15 
            }}
            whileHover={{ x: 4, scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={handleClick}
            className={cn(
                'group relative flex items-start gap-3 p-3.5 rounded-xl cursor-pointer',
                'border transition-all duration-200',
                'hover:shadow-md',
                isSelected 
                    ? 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-500/30'
                    : isUnread
                        ? 'bg-white dark:bg-white/5 border-black/[.07] dark:border-white/[.07] shadow-sm'
                        : 'bg-transparent border-transparent hover:bg-gray-50 dark:hover:bg-white/[0.03]'
            )}
        >
            {isUnread && (
                <motion.div
                    layoutId={`unread-${message.id}`}
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-8 rounded-r-full"
                    style={{ backgroundColor: 'var(--brand)' }}
                />
            )}

            <div className={cn(
                'w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
                config.bgColor,
                config.color
            )}>
                {iconComponents[config.icon]}
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                    <h3 className={cn(
                        'text-[13px] leading-tight truncate',
                        isUnread ? 'font-semibold text-gray-900 dark:text-gray-100' : 'font-medium text-gray-700 dark:text-gray-300'
                    )}>
                        {message.title}
                    </h3>
                    {/* ИСПОЛЬЗУЕМ timeAgo в title для tooltip */}
                    <span 
                        className="text-[10px] text-gray-400 dark:text-gray-500 font-mono shrink-0"
                        title={timeAgo}
                    >
                        {formattedTime}
                    </span>
                </div>
                
                <p className={cn(
                    'text-[12px] mt-1 line-clamp-2',
                    isUnread ? 'text-gray-600 dark:text-gray-400' : 'text-gray-500 dark:text-gray-500'
                )}>
                    {message.body}
                </p>

                {message.metadata && (
                    <div className="flex items-center gap-2 mt-2">
                        {message.metadata.siteName && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 font-mono">
                                {message.metadata.siteName}
                            </span>
                        )}
                        {message.metadata.duration && (
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                {message.metadata.duration} мин
                            </span>
                        )}
                    </div>
                )}
            </div>

            <motion.div 
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity"
            >
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onMarkAsRead?.();
                    }}
                    className="p-1.5 rounded-lg bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-gray-700"
                    title={isUnread ? 'Отметить прочитанным' : 'Отметить непрочитанным'}
                >
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        {isUnread ? (
                            <path d="M18 6L6 18M6 6l12 12"/>
                        ) : (
                            <circle cx="12" cy="12" r="10"/>
                        )}
                    </svg>
                </button>
            </motion.div>
        </motion.div>
    );
};